import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import { getAthleteCombines } from "../../services/AthleteService";
import { setEventId } from "../../store/slices/athleteSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";

export default function AthleteSearchEvent() {
  const r_athlete = useSelector((state) => state.athletes);
  const [combines, setCombines] = useState([]);
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const location = useLocation();

  const fetchCombines = async () => {
    if (!r_athlete.profile.id) return;
    try {
      const data = await getAthleteCombines(apiInstance, r_athlete.profile.id);
      setCombines(data.filter((a) => a.registered));
    } catch (error) {
      console.error("Error fetching combines:", error);
    }
  };

  useEffect(() => {
    fetchCombines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r_athlete.profile.id]);

  useEffect(() => {
    return () => {
      dispatch(setEventId(undefined));
    };
  }, [dispatch, location.pathname]);

  return (
    <div className="flex justify-between items-center w-full pb-0">
      <CombineDropdown
        combines={combines}
        onSelect={(e) => dispatch(setEventId(e))}
      />
    </div>
  );
}

const formatApiDate = (dateStr) => {
  if (!dateStr) return "";

  const [year, month, day] = String(dateStr).split("-");
  if (!year || !month || !day) return dateStr;

  return `${month}/${day}/${year}`;
};

const CombineDropdown = ({ combines, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredCombines = combines.filter(
    (combine) =>
      combine.name.toLowerCase().includes(search.toLowerCase()) ||
      combine.city.toLowerCase().includes(search.toLowerCase()) ||
      combine.date.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (combine) => {
    setSelected(combine);
    setOpen(false);
    onSelect(combine.id);
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  return (
    <div className="flex justify-center items-start w-full p-4">
      <div ref={dropdownRef} className="w-[320px] relative">
        <label
          htmlFor="Combine"
          className="block mb-2 text-sm font-medium text-gray-700"
        >
          Combine
        </label>
        <button
          type="button"
          disabled={disabled}
          className="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 w-full px-3 py-2 flex items-center justify-between bg-white truncate"
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="truncate text-left">
            {selected
              ? `${formatApiDate(selected.date)} - ${truncateText(selected.name, selected.city)}`
              : "Select a Combine"}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="ml-2 text-gray-500"
          />
        </button>

        {open && (
          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg">
            <div className="flex items-center px-2 py-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Search combine..."
                className="w-full pl-2 pr-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-400 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="max-h-80 overflow-y-auto text-sm">
              {filteredCombines.length === 0 && (
                <li className="px-4 py-2 text-gray-500">No results found</li>
              )}
              {filteredCombines.map((combine) => (
                <li
                  key={combine.id}
                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer truncate"
                  onClick={() => handleSelect(combine)}
                >
                  {formatApiDate(combine.date)} -{" "}
                  {truncateText(combine.name, combine.city)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
